/**
 * SetBalanceDialog — quick manual balance edit. Uses the correct helper
 * per source table (see useAccountsData.setBalanceManually).
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function SetBalanceDialog({ open, onOpenChange, account, onSubmit }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && account) {
      const b = account.balance ?? 0;
      setValue(String(Math.abs(b)));
    }
  }, [open, account]);

  const save = async (e) => {
    e?.preventDefault?.();
    const n = Number(value);
    if (!Number.isFinite(n)) { toast.error('Enter a number.'); return; }
    setSaving(true);
    try {
      await onSubmit(account, n);
      toast.success('Balance updated.');
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not update balance.');
    } finally {
      setSaving(false);
    }
  };

  if (!account) return null;
  const label = account.category === 'investments' ? 'Current value'
    : account.category === 'liabilities' ? 'Outstanding balance'
    : 'Current balance';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="accounts-setbalance-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-medium">Set balance</DialogTitle>
          <DialogDescription className="text-xs">
            Manually set the {label.toLowerCase()} for{' '}
            <strong className="text-charcoal">{account.name}</strong>. This records today as the
            date it was confirmed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="setbal-input">{label}</Label>
            <Input
              id="setbal-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              data-testid="accounts-setbalance-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}
                    data-testid="accounts-setbalance-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="accounts-setbalance-save">
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
